<template>
  <div class="container-fluid py-4 bg-light min-vh-100">
    <div class="row mb-4 justify-content-center">
      <div class="col-md-6">
        <div class="card border-0 shadow-sm p-2 rounded-4">
          <WorkSpaceSwitcher v-model="selectedGroupId" />
        </div>
      </div>
    </div>

    <div class="d-flex align-items-center justify-content-between mb-4 px-3">
      <div class="d-flex align-items-center">
        <button @click="$router.back()" class="btn btn-white shadow-sm btn-sm me-3 rounded-circle">
          <i class="fas fa-chevron-left"></i>
        </button>
        <h3 class="mb-0 fw-bold text-gradient">AI Strategy Hub</h3>
      </div>
      
      <div v-if="selectedTasks.length > 0" class="selection-badge animate__animated animate__fadeInDown">
        <span class="badge rounded-pill bg-primary shadow-sm px-3 py-2">
          <i class="fas fa-tasks me-2"></i> {{ selectedTasks.length }} Items Selected
        </span>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-4 mb-4">
        <div class="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
          <div class="card-header bg-white py-3 border-0 d-flex justify-content-between align-items-center">
            <h6 class="mb-0 fw-bold text-muted small tracking-wider uppercase">Active Pipeline</h6>
            <button v-if="selectedTasks.length > 0" @click="clearSelection" class="btn btn-link btn-sm text-danger text-decoration-none">Clear All</button>
          </div>
          
          <div class="card-body p-2 overflow-auto" style="max-height: 600px">
            <div v-for="task in activeTasks" :key="task.id" 
                 @click="toggleTaskSelection(task)"
                 class="task-item p-3 mb-2 rounded-3 border transition-all position-relative"
                 :class="isTaskSelected(task.id) ? 'selected-border bg-light-indigo' : 'bg-white'">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <div class="small fw-bold text-dark mb-1">{{ task.title }}</div>
                  <span class="badge bg-soft-secondary text-muted x-small text-uppercase">{{ task.status }}</span>
                </div>
                <div class="selection-indicator">
                  <i v-if="isTaskSelected(task.id)" class="fas fa-check-circle text-primary animate__animated animate__bounceIn"></i>
                  <div v-else class="check-placeholder"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-8 mb-4">
        <div class="card border-0 shadow-lg rounded-4 h-100 ai-card-main">
          <div class="card-body p-4 d-flex flex-column">
            
            <div v-if="selectedTasks.length === 0" class="text-center py-5 my-auto animate__animated animate__fadeIn">
              <div class="mb-4 pulse-animation">
                <i class="fas fa-brain text-indigo-light" style="font-size: 80px"></i>
              </div>
              <h4 class="fw-bold text-dark">Strategic Analysis</h4>
              <p class="text-muted mx-auto" style="max-width: 400px">
                Select one or more tasks to unlock AI-powered roadmaps, risk matrices, and prioritization logic.
              </p>
            </div>


            <div v-else-if="aiResult" class="ai-output-container animate__animated animate__fadeIn">
  <div class="d-flex justify-content-end mb-2">
    <button @click="copyToClipboard" class="btn btn-sm btn-light text-primary border shadow-sm rounded-pill px-3">
      <i :class="copied ? 'fas fa-check' : 'far fa-copy'"></i> 
      {{ copied ? 'Copied!' : 'Copy Strategy' }}
    </button>
  </div>
  
  <div class="markdown-body" v-html="formattedResult"></div>
</div>

            <div v-else class="h-100 d-flex flex-column">
              <div class="ai-header d-flex align-items-center mb-4">
                <div class="icon-box me-3 bg-primary-soft p-2 rounded-3">
                  <i class="fas fa-bolt text-primary"></i>
                </div>
                <h5 class="fw-bold mb-0">Intelligence Output</h5>
              </div>

              <div class="ai-response-viewport flex-grow-1 bg-faded p-4 rounded-4 mb-4 shadow-inner overflow-auto">
                <div v-if="loading" class="text-center py-5">
                  <div class="spinner-grow text-primary mb-3" role="status"></div>
                  <p class="text-primary fw-medium animate__animated animate__pulse animate__infinite">Synthesizing Strategy...</p>
                </div>
                
                <div v-else-if="aiResult" class="markdown-body animate__animated animate__fadeIn" v-html="formattedResult">
                </div>

                <div v-else class="text-center text-muted py-5 border-dashed rounded-4">
                  <i class="fas fa-robot mb-3 d-block opacity-50" style="font-size: 40px"></i>
                  Ready to process <strong>{{ selectedTasks.length }}</strong> tasks. Choose an action below.
                </div>
              </div>

              <div class="d-flex flex-wrap gap-3">
                <button @click="askAI('prioritize')" :disabled="loading" 
                        class="btn btn-indigo flex-grow-1 py-3 fw-bold shadow-sm transition-all">
                   <i class="fas fa-sort-amount-down me-2"></i> 
                   {{ selectedTasks.length > 1 ? 'Prioritize & Compare' : 'Deep Analysis' }}
                </button>
                <button @click="askAI('roadmap')" :disabled="loading" 
                        class="btn btn-outline-indigo flex-grow-1 py-3 fw-bold shadow-sm">
                   <i class="fas fa-map-signs me-2"></i> Roadmap
                </button>
                <button @click="askAI('risks')" :disabled="loading" 
                        class="btn btn-outline-warning flex-grow-1 py-3 fw-bold shadow-sm">
                   <i class="fas fa-shield-alt me-2"></i> Risks
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { tasksService } from '@/services/tasksService';
import WorkSpaceSwitcher from "@/components/WorkSpaceSwitcher.vue";
import { marked } from 'marked'; // IMPORT: Library for Markdown to HTML conversion
import Swal from 'sweetalert2';
export default {
  name: "AIAssistant",
  components: { WorkSpaceSwitcher },
  data() {
    return {
      activeTasks: [],    // Tasks fetched from current workspace
      selectedTasks: [],  // User's temporary selection for analysis
      aiResult: null,     // Raw response string from AI
      loading: false,     // Loading state indicator
      selectedGroupId: null,
      copied:false,
    };
  },
  computed: {
    /**
     * FORMATED RESULT: Converts AI raw text into sanitized and styled HTML
     * using the 'marked' library.
     */
    formattedResult() {
      if (!this.aiResult) return "";
      
      // Configure marked for line breaks and GitHub-style formatting
      marked.setOptions({
        breaks: true,
        gfm: true
      });

      return marked.parse(this.aiResult);
    }
  },
  watch: {
    // Re-fetch tasks whenever the workspace/group changes
    selectedGroupId() {
      this.fetchTasks();
      this.clearSelection();
    },
  },
  methods: {
    /**
     * FETCH TASKS: Retrieve active tasks from service.
     */
    async fetchTasks() {
      try {
        const data = await tasksService.getAllTasks(this.selectedGroupId);
        this.activeTasks = data.filter(t => t.status.toLowerCase() !== 'done');
      } catch (error) {
        console.error("Task Retrieval Error:", error);
      }
    },
    async copyToClipboard() {
    try {
      // We copy the raw text, not the HTML
      await navigator.clipboard.writeText(this.aiResult);
      this.copied = true;
      // Reset the button icon after 2 seconds
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  },

    /**
     * TOGGLE SELECTION: Manage task IDs in the analysis bucket.
     */
    toggleTaskSelection(task) {
      const index = this.selectedTasks.findIndex(t => t.id === task.id);
      if (index > -1) {
        this.selectedTasks.splice(index, 1);
      } else {
        this.selectedTasks.push(task);
      }
      this.aiResult = null; // Clear view when selection changes
    },

    isTaskSelected(taskId) {
      return this.selectedTasks.some(t => t.id === taskId);
    },

    clearSelection() {
      this.selectedTasks = [];
      this.aiResult = null;
    },

    /**
     * ASK AI: Core logic to call the backend with selected task data.
     */
   

     async askAI(actionType) {
  if (this.selectedTasks.length === 0) {
    this.triggerToast('Please select at least one task', 'warning');
    return;
  }

 
  const apiKey = localStorage.getItem('user_groq_key');
  const aiModel = localStorage.getItem('user_ai_model');

  
  if (!apiKey) {
    Swal.fire({
      icon: 'info',
      title: 'API Key Missing',
      text: 'Please enter your Groq API key in the settings panel first.',
      confirmButtonColor: '#6366f1'
    });
    return;
  }

  this.loading = true;
  this.aiResult = null;

  try {
    
    const response = await tasksService.analyzeTask({
      tasks: this.selectedTasks,
      action: actionType,
      groupId: this.selectedGroupId,
      
      userConfig: {
        apiKey: apiKey,
        model: aiModel || 'llama-3.1-70b-versatile'  
      }
    });
    console.log(apiKey , aiModel);
    this.aiResult = response.result;
  } catch (error) {
    console.error("AI Error:", error);
    this.aiResult = "### Strategic Engine Error\n* **Status:** Service unavailable.\n* **Reason:** Possible invalid API key or network timeout. Please check your credentials.";
  } finally {
    this.loading = false;
  }
}

  },
  mounted() {
    this.fetchTasks();
  }
};
</script>

<style scoped>
/* COLOR TOKENS */
.text-indigo { color: #6366f1; }
.text-indigo-dark { color: #4338ca; }
.bg-indigo-light { background-color: #e0e7ff; }
.bg-primary-soft { background-color: rgba(99, 102, 241, 0.1); }
.text-indigo-light { color: #a5b4fc; }
.bg-light-indigo { background-color: #f8faff; }
.bg-faded { background-color: #fcfcfd; }

/* LAYOUT & TYPOGRAPHY */
.text-gradient {
  background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* MARKDOWN STYLING (Injecting styles into v-html content) */
.markdown-body {
  color: #334155;
  line-height: 1.7;
  font-size: 0.95rem;
}
.markdown-body :deep(h1), 
.markdown-body :deep(h2), 
.markdown-body :deep(h3),
.markdown-body :deep(h4),
.markdown-body :deep(h5) {
  color: #4338ca;
  margin-top: 1.25rem;
  font-weight: 700;
}
.markdown-body :deep(ul), .markdown-body :deep(ol) {
  padding-left: 1.25rem;
  margin-bottom: 1rem;
}
.markdown-body :deep(li) {
  margin-bottom: 0.4rem;
}
.markdown-body :deep(strong) {
  color: #1e293b;
}
.markdown-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}
.markdown-body :deep(th), .markdown-body :deep(td) {
  border: 1px solid #e2e8f0;
  padding: 0.75rem;
}
.markdown-body :deep(th) {
  background: #f8fafc;
  color: #6366f1;
}

/* INTERACTIVE ELEMENTS */
.task-item { 
  cursor: pointer; 
  border: 1.5px solid #f1f3f5; 
  transition: all 0.25s ease; 
}
.task-item:hover { 
  transform: translateX(5px); 
  border-color: #6366f1; 
}
.selected-border { 
  border-color: #6366f1 !important; 
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.12); 
}

.ai-response-viewport {
  border: 1px solid #edf2f7;
  min-height: 450px;
  border-radius: 1.2rem;
}

/* ANIMATIONS */
.pulse-animation { animation: pulse 2.5s infinite ease-in-out; }
@keyframes pulse {
  0% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
  100% { opacity: 0.6; transform: scale(1); }
}

.check-placeholder { width: 22px; height: 22px; border: 2px solid #e2e8f0; border-radius: 50%; }
.border-dashed { border: 2px dashed #e2e8f0 !important; }
</style>